import { useCallback, useEffect, useRef } from 'react';

type ExpireFn = () => void | Promise<void>;

export function useExpirationTimer(onExpire: ExpireFn) {
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const onExpireRef = useRef<ExpireFn>(onExpire);

    useEffect(() => {
        onExpireRef.current = onExpire;
    }, [onExpire]);

    const disable = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    const startForMs = useCallback(
        (ms: number) => {
            disable();
            timeoutRef.current = setTimeout(() => {
                timeoutRef.current = null;

                Promise.resolve(onExpireRef.current()).catch((err) => {
                    console.error('Expire handler failed:', err);
                });
            }, Math.max(0, ms));
        },
        [disable],
    );

    const enableUntil = useCallback(
        (expiresAt: Date | string) => {
            const exp = expiresAt instanceof Date ? expiresAt.getTime() : Date.parse(expiresAt);
            startForMs(exp - Date.now());
        },
        [startForMs],
    );

    // cleanup on unmount
    useEffect(() => disable, [disable]);

    return { enableUntil, disable };
}
